import React, { useState, useEffect, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { projectService as towerProjectService } from '../services/projectService';
import { verveContactService } from '@/services/VerveContactService';
import { showSuccessMessage } from '@/lib/toast';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/FormComponents';

export const ProjectManagerAssignmentForm = ({ project, onUpdate, onClose }) => {
   const actualProject = project?.data?.data || project?.data || project;
   const [managers, setManagers] = useState([]);
   
   const [selectedManagers, setSelectedManagers] = useState(() => {
      if (!actualProject?.projectManager) return [];
      if (Array.isArray(actualProject.projectManager)) {
         return actualProject.projectManager.map(m => m._id || m);
      }
      return [actualProject.projectManager._id || actualProject.projectManager];
   });
   const [notes, setNotes] = useState(actualProject.pmAssignmentNotes || '');
   const [loading, setLoading] = useState(false);
   const [search, setSearch] = useState('');

   useEffect(() => {
      const fetchManagers = async () => {
         try {
            const response = await towerProjectService.getProjectManagers();
            const rawManagers = Array.isArray(response) ? response : (response?.data?.data || response?.data || []);
            const activeManagers = rawManagers.filter((u) => u?.status !== 'Inactive');
            setManagers(activeManagers);
         } catch (err) {
            console.error(err);
         }
      };
      fetchManagers();
   }, []);

   const toggleManager = (id) => {
      setSelectedManagers(prev => 
         prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
      );
   };

   const filteredManagers = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return managers;
      return managers.filter(
         (m) =>
            (m.name || '').toLowerCase().includes(q) ||
            (m.phone || '').toLowerCase().includes(q)
      );
   }, [managers, search]);

   const handleAssign = async () => {
      if (!selectedManagers.length) return;

      setLoading(true);
      try {
         const actualProject = project?.data?.data || project?.data || project;
         const projectId = actualProject?._id || actualProject?.id || actualProject?.customId || actualProject?.projectId || actualProject?.verveId;
         
         if (!projectId) {
            console.error("Project object structure:", project);
            throw new Error("Project ID is missing from the project object.");
         }

         await verveContactService.updateContact(projectId, { 
            projectManager: selectedManagers,
            pmAssignmentNotes: notes,
         });
         
         const res = await towerProjectService.getProjectById(projectId);
         const updatedProject = res?.data?.data || res?.data || res;
         
         if (onUpdate && updatedProject) onUpdate(updatedProject);
         if (onClose) onClose();
         showSuccessMessage("Project Manager assigned successfully");
      } catch (error) {
         console.error(error);
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="flex flex-col gap-4 font-poppins">
         {/* Selected count badge */}
         <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md transition-all">
            <span className={`w-5 h-5 rounded-full text-white text-[10px] font-black flex items-center justify-center transition-colors ${
               selectedManagers.length > 0 ? 'bg-[#64748b]' : 'bg-slate-300'
            }`}>
               {selectedManagers.length}
            </span>
            <span className="text-[12px] font-semibold text-slate-600">
               {selectedManagers.length === 0
                  ? '0 managers selected'
                  : selectedManagers.length === 1
                  ? '1 manager selected'
                  : `${selectedManagers.length} managers selected`}
            </span>
         </div>

         {/* Multi-select checkbox list */}
         <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-slate-500">Select Project Manager</label>

            {/* Search input */}
            <div className="relative">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
               <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or phone..."
                  className="w-full h-[35px] pl-8 pr-8 text-[12px] border border-slate-200 rounded-md text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:border-[#64748b] focus:ring-[#64748b]/20 transition-all bg-white"
               />
               {search && (
                  <button
                     type="button"
                     onClick={() => setSearch('')}
                     className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                     <X className="w-3.5 h-3.5" />
                  </button>
               )}
            </div>

            <div className="flex flex-col border border-slate-200 rounded-md overflow-hidden max-h-[189px] overflow-y-auto">
               {managers.length === 0 ? (
                  <div className="px-4 py-6 text-center text-[12px] text-slate-400 italic">
                     No project managers found.
                  </div>
               ) : filteredManagers.length === 0 ? (
                  <div className="px-4 py-6 text-center text-[12px] text-slate-400 italic">
                     No results for &ldquo;{search}&rdquo;
                  </div>
               ) : (
                  filteredManagers.map((m) => {
                     const isChecked = selectedManagers.includes(m._id || m);
                     return (
                        <label
                           key={m._id || m}
                           onClick={() => toggleManager(m._id || m)}
                           className={`flex items-center gap-3 px-3.5 py-2.5 border-b border-slate-100 last:border-b-0 cursor-pointer select-none transition-colors
                              ${isChecked ? 'bg-slate-50/80 font-medium' : 'hover:bg-slate-50/50'}`}
                        >
                           <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}}
                              className="w-4 h-4 rounded border-slate-300 text-[#64748b] focus:ring-0 cursor-pointer"
                           />

                           {/* Avatar initials */}
                           <div className={`w-7 h-7 rounded flex items-center justify-center text-[11px] font-bold flex-shrink-0 transition-all
                              ${isChecked ? 'bg-[#64748b] text-white' : 'bg-slate-100 text-slate-500'}`}
                           >
                              {(m.name || '?').charAt(0).toUpperCase()}
                           </div>

                           {/* Name & phone */}
                           <div className="flex flex-col min-w-0 flex-1">
                              <span className={`text-[13px] font-semibold truncate transition-colors
                                 ${isChecked ? 'text-slate-800' : 'text-slate-600'}`}>
                                 {m.name}
                              </span>
                              {m.phone && (
                                 <span className="text-[11px] text-slate-400 font-medium">{m.phone}</span>
                              )}
                           </div>

                           {/* Selected pill */}
                           {isChecked && (
                              <span className="text-[10px] font-bold text-[#64748b] bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">
                                 Selected
                              </span>
                           )}
                        </label>
                     );
                  })
               )}
            </div>
         </div>

         {/* Notes Textarea */}
         <div className="flex flex-col gap-1.5 mt-1">
            <label className="text-[13px] font-medium text-slate-500">Assignment Notes (Optional)</label>
            <Textarea 
               rows={3}
               value={notes} 
               onChange={(e) => setNotes(e.target.value)}
               placeholder="Add special instructions or context for the manager..."
               className="text-[13px] resize-none border-slate-200 focus:border-[#64748b]"
            />
         </div>

         {/* Action buttons using Button component variants */}
         <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-4 border-t border-slate-100">
            <Button
               variant="secondary"
               onClick={onClose}
               className="w-full sm:flex-1"
            >
               Cancel
            </Button>
            <Button
               variant="orange"
               onClick={handleAssign}
               disabled={!selectedManagers.length || loading}
               loading={loading}
               className="w-full sm:flex-1"
            >
               {loading ? '' : 'Confirm Assignment'}
            </Button>
         </div>
      </div>
   );
};
